// File: frontend/src/pages/students/StudentNavigator.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/axios';

const StudentNavigator = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fast search effect triggering as user types
   */
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchStudentResults(searchTerm);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  /**
   * Fetch matching students from API as query changes
   * @param {string} query - Live search query string
   */
  const fetchStudentResults = async (query) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/students', {
        params: {
          search: query,
          page: 1,
          limit: 15
        }
      });

      if (response.data?.success) {
        setStudents(response.data.data || []);
      } else {
        setError(response.data?.message || 'Failed to search students.');
      }
    } catch (err) {
      console.error('Error in Quick Navigator search:', err);
      setError('Error communicating with server.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Return status badge style for fast display
   */
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800';
      case 'inactive':
        return 'bg-slate-100 text-slate-700';
      case 'transferred':
        return 'bg-blue-100 text-blue-800';
      case 'graduated':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col">
      {/* Top Header */}
      <header className="border-b border-slate-800 bg-slate-900/90 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/students" className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-white text-lg leading-tight">Student Navigator</h1>
              <p className="text-xs text-slate-400">Instant real-time search & navigation</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Search View */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-8">
        
        {/* Instant Search Bar Box */}
        <div className="relative mb-8">
          <input
            type="text"
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Type student name, father name, or ID (e.g. STD-2026-001)..."
            className="w-full pl-12 pr-10 py-4 bg-slate-800 border-2 border-slate-700 rounded-2xl text-white placeholder-slate-400 text-base sm:text-lg focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 transition shadow-xl"
          />
          <svg className="w-6 h-6 text-slate-400 absolute left-4 top-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-4 text-slate-400 hover:text-white font-bold"
            >
              ✕
            </button>
          )}
        </div>

        {/* Status Indicator */}
        <div className="flex items-center justify-between mb-4 text-xs text-slate-400">
          <span>{loading ? 'Searching...' : `Found ${students.length} matching students`}</span>
          <span>Click student to view profile</span>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-800 text-red-300 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Results Card List */}
        {loading ? (
          <div className="py-12 text-center text-slate-500 flex flex-col items-center gap-3">
            <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Searching student directory...</span>
          </div>
        ) : students.length === 0 ? (
          <div className="bg-slate-800/50 rounded-2xl p-12 text-center border border-slate-800">
            <p className="text-slate-400 text-sm">No student records found matching "{searchTerm}".</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {students.map((student) => (
              <div
                key={student.id}
                onClick={() => navigate(`/students/${student.id}`)}
                className="bg-slate-800 hover:bg-slate-700/80 border border-slate-700/60 rounded-2xl p-4 cursor-pointer transition shadow-md hover:shadow-xl hover:scale-[1.01] flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 text-primary border border-primary/30 flex items-center justify-center font-extrabold text-base">
                    {student.student_name ? student.student_name.slice(0, 2).toUpperCase() : 'ST'}
                  </div>
                  <div>
                    <h3 className="font-bold text-white group-hover:text-primary transition text-base leading-snug">
                      {student.student_name}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Class <span className="text-slate-200 font-semibold">{student.class_id}</span> • ID: <span className="font-mono text-slate-300">{student.student_id}</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadge(student.status)}`}>
                    {student.status || 'Active'}
                  </span>
                  <svg className="w-5 h-5 text-slate-500 group-hover:text-white transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}

      </main>
    </div>
  );
};

export default StudentNavigator;
